import { IonButton, IonIcon } from '@ionic/react';
import { openOutline } from 'ionicons/icons';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalDate } from '../../../config/utils/dateHelpers';
import { CURRENCY } from '../../../interfaces/enumerables';
import { InvoiceInList } from '../../../interfaces/invoice.interface';
import { PATHS } from '../../../navigation/data';

interface Props {
  index: number;
  data: InvoiceInList;
  onSelect: (data: InvoiceInList) => void;
  isLoading?: boolean;
  alreadySelected?: boolean;
}

export const CorrelatedInvoicePickerCard = ({ index, data, onSelect, isLoading, alreadySelected }: Props) => {
  const { t } = useTranslation();
  const currencySymbol = useMemo(() => CURRENCY[data?.currency || 'EUR'], [data?.currency]);

  const handleOpenInTab = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      window.open(`${PATHS.SALES}/view/${data.id}`, '_blank');
    },
    [data.id],
  );

  const handleSelect = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(data);
    },
    [data, onSelect],
  );

  return (
    <div
      style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '12px 16px' }}
      data-testid={`picker_card_${index}`}
      data-aade-code={data?.documentType?.aadeCode}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        {/* Left: invoice info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                opacity: 0.85,
                background: 'rgba(255,255,255,0.08)',
                borderRadius: 4,
                padding: '2px 6px',
                color: data?.post_with_provider ? '#4f8ef7' : undefined,
              }}>
              {t(`DOC_TYPES.${data?.documentType?.name}`)}
            </span>
            <span style={{ fontSize: 12, opacity: 0.5 }}>
              {data.seriesName}/{data.serialNumber}/{data.year}
            </span>
          </div>

          <div style={{ fontSize: 13, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {data?.customer?.companyName}
          </div>

          <div style={{ display: 'flex', gap: 12, fontSize: 12, opacity: 0.65 }}>
            <span>{data?.totalValue} {currencySymbol}</span>
            <span>{getLocalDate(data?.documentDate)}</span>
          </div>

          <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4, fontFamily: 'monospace' }}>
            MARK: {data.mark}
          </div>
        </div>

        {/* Right: action buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between', flexShrink: 0, alignSelf: 'stretch' }}>
          <IonButton
            size="small"
            fill="clear"
            onClick={handleOpenInTab}
            title={t('SALE.OPEN_INVOICE_TAB')}
            style={{ '--padding-start': '4px', '--padding-end': '4px', margin: 0 }}>
            <IonIcon icon={openOutline} style={{ fontSize: 16 }} />
          </IonButton>
          <IonButton
            size="small"
            fill={alreadySelected ? 'outline' : 'solid'}
            disabled={alreadySelected || isLoading}
            onClick={handleSelect}
            style={{ margin: 0 }}>
            {alreadySelected ? '✓' : t('SALE.SELECT_INVOICE')}
          </IonButton>
        </div>
      </div>
    </div>
  );
};
